import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import Cookies from 'js-cookie';
import '../styles/Files.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  useProfileBookingId,
  useProfileProjectId,
  useProfileRefresh,
} from '../ProfileBookingContext';
import ProjectWorkReportModal from '../../Customer/components/projectDetail/ProjectWorkReportModal';
import '../../Customer/styles/CustomerProjectDetail.css';
import '../../Customer/styles/ProjectFilesCentre.css';

const APPROVED_WORK_REPORTS_QUERY = `
  query ApprovedWorkReportsForBooking($bookingId: String!, $projectId: String) {
    approvedWorkReportsForBooking(bookingId: $bookingId, projectId: $projectId) {
      id
      date
      timeIn
      timeOut
      duration
      employeeName
      userId
      notesJson
      projectCode
      projectName
      approvalStatus
      approvedByName
      workReportId
      photosJson
    }
  }
`;

const RESOLVE_BOOKING_FILE_URLS = `
  query ResolveBookingFileUrls($bookingId: String!, $keys: [String!]!) {
    resolveBookingFileUrls(bookingId: $bookingId, keys: $keys)
  }
`;

function parseWorkReportNotes(json) {
  try {
    const o = JSON.parse(json || '{}');
    return o && typeof o === 'object' && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

function collectReportImages(notes) {
  const out = [];
  const push = (dataUrl, name) => {
    if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
      out.push({ name: name || 'Photo', dataUrl });
    }
  };
  push(notes.beforePictureDataUrl, notes.beforePictureName);
  push(notes.afterPictureDataUrl, notes.afterPictureName);
  push(notes.resourcePictureDataUrl, notes.resourcePictureName);
  ['beforePictures', 'afterPictures', 'resourcePictures'].forEach((key) => {
    const arr = notes[key];
    if (Array.isArray(arr)) {
      arr.forEach((im, i) => push(im?.dataUrl, im?.name || `${key}-${i + 1}`));
    }
  });
  const manual = notes.manualReport || {};
  ['beforePictures', 'afterPictures', 'resourcePictures'].forEach((key) => {
    const arr = manual[key];
    if (Array.isArray(arr)) {
      arr.forEach((im, i) => push(im?.dataUrl, im?.name || `manual-${key}-${i + 1}`));
    }
  });
  return out;
}

function storedPhotoCount(report) {
  try {
    const photos = JSON.parse(report?.photosJson || '[]');
    return Array.isArray(photos) ? photos.filter((photo) => photo?.url || photo?.key).length : 0;
  } catch {
    return 0;
  }
}

function format12(value) {
  if (!value || typeof value !== 'string') return '—';
  const [hRaw, mRaw = '00'] = value.split(':');
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (Number.isNaN(h) || Number.isNaN(m)) return value;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

export default function Files({ bookingId: bookingIdProp, projectId: projectIdProp }) {
  const profileBookingId = useProfileBookingId();
  const profileProjectId = useProfileProjectId();
  const { refreshToken } = useProfileRefresh();

  const resolveBookingId = useCallback(() => {
    const id =
      (bookingIdProp && String(bookingIdProp).trim()) ||
      (profileBookingId && String(profileBookingId).trim()) ||
      Cookies.get('bookingId') ||
      '';
    return id || null;
  }, [bookingIdProp, profileBookingId]);

  const resolveProjectId = useCallback(() => {
    const id =
      (projectIdProp && String(projectIdProp).trim()) ||
      (profileProjectId && String(profileProjectId).trim()) ||
      '';
    return id || null;
  }, [projectIdProp, profileProjectId]);

  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [companyFiles, setCompanyFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());
  const [workReports, setWorkReports] = useState([]);
  const [workReportsLoading, setWorkReportsLoading] = useState(false);
  const [activeWorkReport, setActiveWorkReport] = useState(null);
  const [bookingEmail, setBookingEmail] = useState('');
  const [sendingReportId, setSendingReportId] = useState(null);

  const fetchBookingFiles = useCallback(async () => {
    const id = resolveBookingId();
    if (!id) {
      setUploadedFiles([]);
      setCompanyFiles([]);
      setBookingEmail('');
      setError('');
      return;
    }

    // Keep cookie in sync so upload/update mutations match pipeline behaviour.
    try {
      Cookies.set('bookingId', id);
    } catch {
      /* ignore */
    }

    setFilesLoading(true);
    setError('');
    try {
      const query = `
      query FindOneBooking($id: String!) {
        findOneBooking(id: $id, includeFileUrls: true) {
          id
          fullName
          email
          signUrls
          companyUpload
          uploads
          CompanyUploads
        }
      }
    `;

      const data = await graphqlRequest(query, { id });

      const booking = data?.findOneBooking || null;
      setBookingEmail(String(booking?.email || '').trim());
      const signUrls = Array.isArray(booking?.signUrls) ? booking.signUrls : [];
      const companyUpload = Array.isArray(booking?.companyUpload)
        ? booking.companyUpload
        : [];
      const uploadKeys = Array.isArray(booking?.uploads) ? booking.uploads : [];
      const companyUploadKeys = Array.isArray(booking?.CompanyUploads)
        ? booking.CompanyUploads
        : [];

      // Fallback: if computed fields are empty but keys exist, resolve via backend helper.
      const [resolvedSignUrls, resolvedCompanyUpload] = await Promise.all([
        signUrls.length === 0 && uploadKeys.length > 0
          ? (async () => {
              const r = await graphqlRequest(RESOLVE_BOOKING_FILE_URLS, {
                bookingId: resolveBookingId(), keys: uploadKeys,
              });
              return Array.isArray(r?.resolveBookingFileUrls)
                ? r.resolveBookingFileUrls
                : [];
            })()
          : Promise.resolve(signUrls),
        companyUpload.length === 0 && companyUploadKeys.length > 0
          ? (async () => {
              const r = await graphqlRequest(RESOLVE_BOOKING_FILE_URLS, {
                bookingId: resolveBookingId(), keys: companyUploadKeys,
              });
              return Array.isArray(r?.resolveBookingFileUrls)
                ? r.resolveBookingFileUrls
                : [];
            })()
          : Promise.resolve(companyUpload),
      ]);

      setUploadedFiles(resolvedSignUrls || []);
      setCompanyFiles(resolvedCompanyUpload || []);
      setImageLoadErrors(new Set());
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load files');
      setUploadedFiles([]);
      setCompanyFiles([]);
      setBookingEmail('');
    } finally {
      setFilesLoading(false);
    }
  }, [resolveBookingId]);

  const fetchWorkReports = useCallback(async () => {
    const id = resolveBookingId();
    if (!id) {
      setWorkReports([]);
      return;
    }
    setWorkReportsLoading(true);
    try {
      const projectId = resolveProjectId();
      const data = await graphqlRequest(APPROVED_WORK_REPORTS_QUERY, {
        bookingId: id,
        projectId: projectId || null,
      });
      setWorkReports(
        Array.isArray(data?.approvedWorkReportsForBooking)
          ? data.approvedWorkReportsForBooking
          : [],
      );
    } catch (e) {
      console.error('approvedWorkReportsForBooking:', e);
      setWorkReports([]);
    } finally {
      setWorkReportsLoading(false);
    }
  }, [resolveBookingId, resolveProjectId]);

  useEffect(() => {
    fetchBookingFiles();
    fetchWorkReports();
  }, [fetchBookingFiles, fetchWorkReports, refreshToken]);

  useEffect(() => {
    if (!activeWorkReport) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActiveWorkReport(null);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [activeWorkReport]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDelete = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const generateUploadUrls = async (fileList) => {
    const query = `
      mutation GenerateBookingFileUrls($files: [Fileupload!]!) {
        generateBookingFileUrlsForBooking(files: $files) {
          key
          url
        }
      }
    `;

    const variables = {
      files: fileList.map((f) => ({ fileName: f.name, contentType: f.type })),
    };

    const data = await graphqlRequest(query, variables);
    return data?.generateBookingFileUrlsForBooking || [];
  };

  const uploadFileToS3 = async (file, signedUrl) => {
    try {
      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': file.type },
      });
    } catch (uploadErr) {
      throw new Error(`Upload failed: ${file.name}`);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return setError('Please select files');

    setLoading(true);
    setError('');

    try {
      const bid = resolveBookingId();
      if (!bid) return setError('Missing booking ID');

      const uploadData = await generateUploadUrls(files);

      await Promise.all(uploadData.map((u, i) => uploadFileToS3(files[i], u.url)));

      const mutation = `
      mutation UpdateBooking($updateBookingInput: UpdateBookingInput!, $fileKeys: [String!]) {
        updateBooking(updateBookingInput: $updateBookingInput, fileKeys: $fileKeys) {
          id
          uploads
        }
      }
    `;

      await graphqlRequest(mutation, {
        updateBookingInput: { id: bid },
        fileKeys: uploadData.map((u) => u.key),
      });

      setFiles([]);
      await fetchBookingFiles();
      toast.success('Files uploaded successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendWorkReport = (report) => {
    if (!report?.id) return;
    const bid = resolveBookingId();
    const to = String(bookingEmail || '').trim();
    if (!bid) {
      toast.error('Booking ID not found');
      return;
    }
    if (!to) {
      toast.error('Client email not found on this lead');
      return;
    }
    setActiveWorkReport(report);
    setSendingReportId(report.id);
  };

  const getFileName = (url) => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return decodeURIComponent(pathParts[pathParts.length - 1]);
    } catch {
      const parts = url.split('/');
      return parts[parts.length - 1].split('?')[0];
    }
  };

  const handleImageError = (url, index) => {
    setImageLoadErrors((prev) => new Set([...prev, url]));
  };

  const renderFile = (url, index) => {
    const fileName = getFileName(url);
    const hasError = imageLoadErrors.has(url);

    return (
      <div key={index} className="file-item">
        {!hasError ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img
              src={url}
              alt={fileName}
              className="preview-img"
              onError={() => handleImageError(url, index)}
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                objectFit: 'cover',
              }}
            />
          </a>
        ) : (
          <a href={url} target="_blank" rel="noopener noreferrer" className="file-name">
            📄 {fileName}
          </a>
        )}
      </div>
    );
  };

  const renderWorkReportCard = (report) => {
    const notes = parseWorkReportNotes(report.notesJson);
    const manual = notes.manualReport || {};
    const summary =
      notes.reportSummary || manual.summary || notes.workStatus || '';
    const workStatus = notes.workStatus || manual.workStatus;
    const projectStatus = notes.projectStatus || manual.projectStatus;
    const siteLocation = notes.siteLocation;
    const managerFeedback = notes.managerFeedback;
    const completedDetails = Array.isArray(manual.completedTaskDetails)
      ? manual.completedTaskDetails
      : [];
    const completedIds = Array.isArray(manual.completedTasks) ? manual.completedTasks : [];
    const images = collectReportImages(notes);
    const photoTotal = storedPhotoCount(report) || images.length;

    const onSend = () => sendWorkReport(report);

    const openView = () => setActiveWorkReport(report);

    const taskCount =
      completedDetails.length > 0
        ? completedDetails.length
        : completedIds.length > 0
          ? completedIds.length
          : 0;

    return (
      <div key={report.id} className="wr-card">
        <div className="wr-card-head">
          <div>
            <strong>
              {report.projectName || report.projectCode || 'Work report'}{' '}
            </strong>
            <span className="wr-muted">
              ({report.date}) · {report.employeeName}
            </span>
          </div>
          <span className="wr-badge-approved">Approved</span>
        </div>
        <div className="wr-grid">
          <div>
            <span className="wr-label">Shift</span>
            <p>
              {format12(report.timeIn)} – {format12(report.timeOut)}
            </p>
          </div>
          {siteLocation ? (
            <div>
              <span className="wr-label">Site</span>
              <p>{siteLocation}</p>
            </div>
          ) : null}
          {(workStatus || projectStatus) && (
            <div>
              <span className="wr-label">Status</span>
              <p>
                {[workStatus, projectStatus].filter(Boolean).join(' · ') || '—'}
              </p>
            </div>
          )}
        </div>
        {summary ? (
          <div className="wr-block">
            <span className="wr-label">Summary</span>
            <p className="wr-text">{String(summary)}</p>
          </div>
        ) : null}
        <div className="wr-preview-meta">
          {taskCount > 0 ? <span>{taskCount} task(s)</span> : <span>No tasks</span>}
          <span className="wr-dot">•</span>
          {photoTotal > 0 ? <span>{photoTotal} photo(s)</span> : <span>No photos</span>}
          {managerFeedback ? (
            <>
              <span className="wr-dot">•</span>
              <span>Has manager feedback</span>
            </>
          ) : null}
        </div>

        <div className="wr-actions">
          <button type="button" className="wr-view-btn" onClick={openView}>
            View
          </button>
          <button
            type="button"
            className="wr-send-btn"
            onClick={onSend}
            disabled={sendingReportId === report.id}
          >
            {sendingReportId === report.id ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    );
  };

  const renderWorkReportModal = () => {
    if (!activeWorkReport) return null;
    const report = activeWorkReport;
    const title =
      [report.projectCode, report.projectName].filter(Boolean).join(' · ') ||
      'Work report';
    return (
      <ProjectWorkReportModal
        reports={[report]}
        title={title}
        bookingId={resolveBookingId()}
        projectId={resolveProjectId() || parseWorkReportNotes(report.notesJson).projectId}
        clientEmail={bookingEmail}
        fileStem={`${report.projectCode || 'work-report'}-${report.date || 'report'}`}
        onClose={() => {
          setActiveWorkReport(null);
          setSendingReportId(null);
        }}
        onReviewed={fetchWorkReports}
        autoEmail={sendingReportId === report.id}
        onAutoEmailDone={() => setSendingReportId(null)}
      />
    );
  };

  return (
    <div className="files-container files-container--pipeline">
      <h2 className="files-title">Files</h2>
      <p className="files-subtitle">Upload and manage your documents.</p>

      {error && <div className="error-message">{error}</div>}

      {!resolveBookingId() ? (
        <p className="wr-muted">
          No linked lead for this project — open the lead in Sales Pipeline or
          attach a booking to view and upload files.
        </p>
      ) : null}

      {filesLoading ? (
        <p className="wr-muted">Loading files…</p>
      ) : null}

      <div className="file-section wr-section">
        <h3>Approved work reports (employee portal)</h3>
        <p className="wr-hint">
          {resolveProjectId()
            ? 'Shows manager-approved work reports for this project only.'
            : "Shows manager-approved work reports from cleaners and contractors for projects linked to this lead. Create a project under the Project tab first; reports match by project code."}
        </p>
        {workReportsLoading ? (
          <p className="wr-muted">Loading work reports…</p>
        ) : workReports.length === 0 ? (
          <p>
            {resolveProjectId()
              ? 'No approved work reports for this project yet.'
              : "No approved work reports for this lead's projects yet."}
          </p>
        ) : (
          <div className="wr-list-wrap">{workReports.map(renderWorkReportCard)}</div>
        )}
      </div>

      {renderWorkReportModal()}

      <div className="file-section">
        <h3>Customer Files</h3>
        {uploadedFiles.length > 0 ? (
          <div className="file-preview">
            {uploadedFiles.map((url, i) => renderFile(url, i))}
          </div>
        ) : (
          <p>No customer files uploaded.</p>
        )}
      </div>

      <div className="file-section">
        <h3>Company Files</h3>
        {companyFiles.length > 0 ? (
          <div className="file-preview">
            {companyFiles.map((url, i) => renderFile(url, i))}
          </div>
        ) : (
          <p>No company files uploaded.</p>
        )}
      </div>

      <hr />

      <h3>Upload New Files</h3>

      <div className="upload-center">
        <label className="upload-btn">
          Upload Files
          <input type="file" multiple onChange={handleFileChange} hidden />
        </label>
      </div>

      <div className="drop-area" onDrop={handleDrop} onDragOver={handleDragOver}>
        <p>Drag & Drop files here</p>
      </div>

      <div className="file-preview">
        {files.map((file, index) => (
          <div key={index} className="file-item">
            {file.type.startsWith('image/') ? (
              <img src={URL.createObjectURL(file)} alt={file.name} className="preview-img" />
            ) : (
              <span className="file-name">{file.name}</span>
            )}
            <button type="button" className="delete-btn" onClick={() => handleDelete(index)}>
              ✕
            </button>
          </div>
        ))}
      </div>

      {files.length > 0 && (
        <div className="upload-button-container">
          <button type="button" onClick={handleUpload} disabled={loading} className="upload-submit-btn">
            {loading ? 'Uploading...' : `Upload ${files.length} file(s)`}
          </button>
        </div>
      )}
    </div>
  );
}
